import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.card, padding: '24px' },
  input: { ...s.input },
  label: { ...s.labelNormal },
  select: { ...s.select },
}

const ICON_PATHS = {
  award: [['circle', { cx: 12, cy: 8, r: 7 }], ['polyline', { points: '8.21 13.89 7 23 12 20 17 23 15.79 13.88' }]],
  eye: [['path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }], ['circle', { cx: 12, cy: 12, r: 3 }]],
  heart: [['path', { d: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' }]],
  star: [['polygon', { points: '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' }]],
  trending: [['polyline', { points: '23 6 13.5 15.5 8.5 10.5 1 18' }], ['polyline', { points: '17 6 23 6 23 12' }]],
  users: [['path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }], ['circle', { cx: 9, cy: 7, r: 4 }], ['path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }], ['path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' }]],
}
const ICON_OPTIONS = Object.keys(ICON_PATHS)

function IconSvg({ name, size = 16, color = colors.textSecondary }) {
  const parts = ICON_PATHS[name] || ICON_PATHS.star
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {parts.map(([tag, attrs], i) => {
        if (tag === 'circle') return <circle key={i} {...attrs} />
        if (tag === 'polyline') return <polyline key={i} {...attrs} />
        if (tag === 'polygon') return <polygon key={i} {...attrs} />
        return <path key={i} {...attrs} />
      })}
    </svg>
  )
}

const DEFAULT_CONFIG = {
  is_active: false,
  bestseller_enabled: true,
  viewing_enabled: true,
  preferred_enabled: true,
  bestseller_max_rank: 3,
  viewing_min: 8,
  viewing_max: 45,
  preferred_audiences: ['kullanıcıların', 'annelerin'],
  category_codes: [],
  bestseller_category_codes: [],
  anchor_selector: '',
  position: 'before',
  bestseller_template: 'Kategorinin en çok satan {rank}. ürünü',
  viewing_template: 'Bu ürünü şu anda {count} kişi inceliyor',
  preferred_template: '{audience} en çok tercih ettiği ürün',
  bestseller_icon: 'award',
  viewing_icon: 'eye',
  preferred_icon: 'heart',
}

export default function SmartView() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [categories, setCategories] = useState([])
  const [tab, setTab] = useState('config')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [newAudience, setNewAudience] = useState('')

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-view-config`, { headers })
      .then(res => { if (res.data && res.data.brand_id) setConfig({ ...DEFAULT_CONFIG, ...res.data }) })
      .catch(() => {})
    axios.get(`${API}/admin/brands/${selectedBrand.id}/categories`, { headers })
      .then(res => setCategories(res.data.flat || []))
      .catch(() => {})
  }, [selectedBrand?.id]) // eslint-disable-line

  async function handleSave() {
    setSaving(true)
    await axios.put(`${API}/admin/brands/${selectedBrand.id}/smart-view-config`, config, { headers })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  function set(key, value) { setConfig(c => ({ ...c, [key]: value })) }

  function toggleCategory(code) {
    const cur = config.category_codes || []
    set('category_codes', cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code])
  }
  function toggleBestsellerCategory(code) {
    const cur = config.bestseller_category_codes || []
    set('bestseller_category_codes', cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code])
  }

  function addAudience() {
    if (!newAudience.trim()) return
    set('preferred_audiences', [...(config.preferred_audiences || []), newAudience.trim()])
    setNewAudience('')
  }
  function removeAudience(i) {
    set('preferred_audiences', (config.preferred_audiences || []).filter((_, idx) => idx !== i))
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ color: colors.textSecondary, marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  const MESSAGES = [
    { on: 'bestseller_enabled', tpl: 'bestseller_template', ic: 'bestseller_icon', title: 'En çok satan', hint: '{rank} → 1..maks arası rastgele sıra', sample: (config.bestseller_template || '').replace('{rank}', '3') },
    { on: 'viewing_enabled', tpl: 'viewing_template', ic: 'viewing_icon', title: 'İnceleyen sayısı', hint: '{count} → min..maks arası rastgele', sample: (config.viewing_template || '').replace('{count}', '28') },
    { on: 'preferred_enabled', tpl: 'preferred_template', ic: 'preferred_icon', title: 'En çok tercih edilen', hint: '{audience} → listeden rastgele', sample: (config.preferred_template || '').replace('{audience}', (config.preferred_audiences || ['kullanıcıların'])[0] || '') },
  ]

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <div>
          <h2 style={s.pageTitle}>Smart View — {selectedBrand.name}</h2>
          <p style={s.pageSubtitle}>Ürün detayında FOMO mesajları</p>
        </div>
        <div className="sx-wrap" style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={ui.status(config.is_active)}>
            <span style={ui.statusDot(config.is_active)} />
            {config.is_active ? 'Aktif' : 'Pasif'}
          </span>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, padding: '10px 20px', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Kaydediliyor...' : saved ? '✓ Kaydedildi' : 'Kaydet'}
          </button>
        </div>
      </div>

      <div style={ui.tabBar}>
        {[
          { key: 'config', label: 'Ayarlar' },
          { key: 'messages', label: 'Mesajlar' },
          { key: 'categories', label: 'Kategoriler' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'config' && (
        <div style={style.card}>
          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Durum</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.is_active} onChange={e => set('is_active', e.target.checked)} />
              Smart View aktif
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Yerleşim elementi (anchor selector)</label>
            <input style={style.input} value={config.anchor_selector || ''} onChange={e => set('anchor_selector', e.target.value)}
              placeholder="#addToCartBtn" />
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '6px' }}>Rozetler bu elementin yanına yerleştirilir (ör. sepete ekle butonu).</p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Konum</label>
            <select style={{ ...style.select, width: '200px', maxWidth: '100%' }} value={config.position || 'before'} onChange={e => set('position', e.target.value)}>
              <option value="before">Elementin üstünde</option>
              <option value="after">Elementin altında</option>
            </select>
          </div>
        </div>
      )}

      {tab === 'messages' && (
        <div style={style.card}>
          {MESSAGES.map((m, idx) => (
            <div key={m.on} style={{ paddingBottom: '18px', marginBottom: '18px', borderBottom: idx < 2 ? `1px solid ${colors.borderLight}` : 'none' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '10px' }}>
                <input type="checkbox" checked={config[m.on] !== false} onChange={e => set(m.on, e.target.checked)} />
                {m.title}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '10px', alignItems: 'center' }}>
                <select style={style.select} value={config[m.ic] || ICON_OPTIONS[0]} onChange={e => set(m.ic, e.target.value)}>
                  {ICON_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <input style={style.input} value={config[m.tpl] || ''} onChange={e => set(m.tpl, e.target.value)} />
              </div>
              <div className="sx-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px', fontSize: '13px', color: colors.textPrimary, background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.sm, padding: '8px 10px' }}>
                <IconSvg name={config[m.ic]} />
                <span className="sx-break" style={{ minWidth: 0 }}>{m.sample}</span>
                <span className="sx-break" style={{ fontSize: '11px', color: colors.textMuted, marginLeft: 'auto', minWidth: 0 }}>{m.hint}</span>
              </div>
            </div>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginTop: '4px' }}>
            <div>
              <label style={style.label}>Maks. sıra</label>
              <input type="number" min="1" style={style.input} value={config.bestseller_max_rank || 3} onChange={e => set('bestseller_max_rank', parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <label style={style.label}>İnceleyen min</label>
              <input type="number" min="1" style={style.input} value={config.viewing_min || 8} onChange={e => set('viewing_min', parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <label style={style.label}>İnceleyen maks</label>
              <input type="number" min="1" style={style.input} value={config.viewing_max || 45} onChange={e => set('viewing_max', parseInt(e.target.value) || 1)} />
            </div>
          </div>

          <div style={{ marginTop: '18px' }}>
            <label style={style.label}>Tercih eden kitleler <span style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 400 }}>({'{audience}'} yerine rastgele biri gelir)</span></label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <input style={{ ...style.input, flex: '1 1 180px', minWidth: 0 }} value={newAudience} onChange={e => setNewAudience(e.target.value)}
                placeholder="örn: annelerin" onKeyDown={e => e.key === 'Enter' && addAudience()} />
              <button onClick={addAudience} style={{ ...s.btnSecondary, whiteSpace: 'nowrap' }}>Ekle</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(config.preferred_audiences || []).map((a, i) => (
                <span key={i} className="sx-break" style={{ ...ui.chip, color: colors.textPrimary, gap: '8px', maxWidth: '100%', whiteSpace: 'normal' }}>
                  {a}
                  <button onClick={() => removeAudience(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'categories' && (
        <>
          <div style={style.card}>
            <label style={style.label}>Çalışacağı kategoriler <span style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 400 }}>(boş bırakılırsa tüm ürünlerde çalışır)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', maxHeight: '340px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '12px' }}>
              {categories.filter(c => c.level === 1 || !c.level).map(cat => (
                <label key={cat.category_code} className="sx-break" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '4px', minWidth: 0 }}>
                  <input type="checkbox" checked={(config.category_codes || []).includes(cat.category_code)} onChange={() => toggleCategory(cat.category_code)} />
                  {cat.category_name}
                </label>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '8px', fontVariantNumeric: 'tabular-nums' }}>Seçili: {(config.category_codes || []).length} kategori</p>
          </div>

          <div style={style.card}>
            <label style={style.label}>"En çok satan" mesajı kategorileri <span style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 400 }}>(boş = genel kuralla aynı; sadece seçili kategorilerde "en çok satan" satırı çıkar)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', maxHeight: '340px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '12px' }}>
              {categories.filter(c => c.level === 1 || !c.level).map(cat => (
                <label key={cat.category_code} className="sx-break" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '4px', minWidth: 0 }}>
                  <input type="checkbox" checked={(config.bestseller_category_codes || []).includes(cat.category_code)} onChange={() => toggleBestsellerCategory(cat.category_code)} />
                  {cat.category_name}
                </label>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '8px', fontVariantNumeric: 'tabular-nums' }}>Seçili: {(config.bestseller_category_codes || []).length} kategori</p>
          </div>
        </>
      )}
    </div>
  )
}
